import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { Alert, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../footer/Footer';

import {
    doUtcDate,
    HTTP_JSON_HEADERS,
    HTTP_METHOD_POST, HTTP_STATUS_CREATED,
    REQUEST_URL,
    USER_CREATE_URL,
    USER_REQUEST_PASSWORD_RESET,
    USER_VERIFY_RESET_TOKEN,
    USER_RESET_PASSWORD
} from "../../global/constants/constants";

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
        width: '450px',
    },
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const ResetPasswordContainer = styled(Stack)(({ theme }) => ({
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    backgroundImage:
        'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
        backgroundImage:
            'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
}));


export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState('');

    const [tokenStatus, setTokenStatus] = useState<'loading' | 'valid' | 'invalid' | 'expired'>('loading');
    const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [resetError, setResetError] = useState('');

    useEffect(() => {
        if (!token) {
            setTokenStatus('invalid');
            return;
        }

        fetch(`${REQUEST_URL}${USER_VERIFY_RESET_TOKEN}/${token}`)
            .then(response => {
                if (response.status === 200) {
                    setTokenStatus('valid');
                } else if (response.status === 410) {
                    setTokenStatus('expired');
                } else {
                    setTokenStatus('invalid');
                }
            })
            .catch(error => {
                console.error('Token verification failed:', error);
                setTokenStatus('invalid');
            });
    }, [token]);

    const validatePassword = (value: string): boolean => {
        if (!value) {
            setPasswordError(true);
            setPasswordErrorMessage('Passwort ist erforderlich.');
            return false;
        }
        if (value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Passwort muss mindestens 6 Zeichen lang sein.');
            return false;
        }
        setPasswordError(false);
        setPasswordErrorMessage('');
        return true;
    };

    const validateConfirmPassword = (value: string): boolean => {
        if (!value) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage('Bitte bestätigen Sie Ihr Passwort.');
            return false;
        }
        if (value !== password) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage('Passwörter stimmen nicht überein.');
            return false;
        }
        setConfirmPasswordError(false);
        setConfirmPasswordErrorMessage('');
        return true;
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setPassword(value);
        validatePassword(value);
        if (confirmPassword) {
            validateConfirmPassword(confirmPassword);
        }
    };

    const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setConfirmPassword(value);
        validateConfirmPassword(value);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const isPasswordValid = validatePassword(password);
        const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

        if (!isPasswordValid || !isConfirmPasswordValid) {
            return;
        }

        setResetStatus('loading');
        setResetError('');

        try {
            const response = await fetch(`${REQUEST_URL}${USER_RESET_PASSWORD}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                    new_password: password,
                }),
            });

            if (response.status === 200) {
                setResetStatus('success');
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            } else {
                const data = await response.json();
                setResetError(data.detail || 'Fehler beim Zurücksetzen des Passworts');
                setResetStatus('error');
            }
        } catch (error) {
            console.error('Reset failed:', error);
            setResetError('Netzwerkfehler. Bitte versuchen Sie es später erneut.');
            setResetStatus('error');
        }
    };

    const renderContent = () => {
        if (tokenStatus === 'loading') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress />
                    <Typography>Token wird überprüft...</Typography>
                </Box>
            );
        }

        if (tokenStatus === 'invalid') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="error">
                        Ungültiger Reset-Token. Bitte fordern Sie einen neuen Link an.
                    </Alert>
                    <Button variant="contained" onClick={() => navigate('/')}>
                        Zur Anmeldung
                    </Button>
                </Box>
            );
        }

        if (tokenStatus === 'expired') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="warning">
                        Der Reset-Token ist bereits abgelaufen. Bitte fordern Sie einen neuen Link an.
                    </Alert>
                    <Button variant="contained" onClick={() => navigate('/')}>
                        Zur Anmeldung
                    </Button>
                </Box>
            );
        }

        if (resetStatus === 'success') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="success">
                        Passwort erfolgreich zurückgesetzt! Sie werden in Kürze zur Anmeldeseite weitergeleitet...
                    </Alert>
                </Box>
            );
        }

        return (
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >


                <Alert severity="success">
                        Der Reset-Token ist gültig. Bitte setzen Sie Ihr Passwort zurück.
                </Alert>

                {resetStatus === 'error' && (
                    <Alert severity="error">{resetError}</Alert>
                )}


                <FormControl>
                    <FormLabel htmlFor="password">Neues Passwort</FormLabel>
                    <TextField
                        required
                        fullWidth
                        name="password"
                        placeholder="••••••"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        variant="outlined"
                        error={passwordError}
                        helperText={passwordErrorMessage}
                        value={password}
                        onChange={handlePasswordChange}
                        onBlur={() => validatePassword(password)}
                        disabled={resetStatus === 'loading'}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel htmlFor="confirm-password">Passwort bestätigen</FormLabel>
                    <TextField
                        required
                        fullWidth
                        name="confirm-password"
                        placeholder="••••••"
                        type="password"
                        id="confirm-password"
                        autoComplete="new-password"
                        variant="outlined"
                        error={confirmPasswordError}
                        helperText={confirmPasswordErrorMessage}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        onBlur={() => validateConfirmPassword(confirmPassword)}
                        disabled={resetStatus === 'loading'}
                    />
                </FormControl>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={resetStatus === 'loading'}
                >
                    {resetStatus === 'loading' ? 'Wird gesetzt...' : 'Passwort zurücksetzen'}
                </Button>

                <Button
                    fullWidth
                    variant="text"
                    onClick={() => navigate('/')}
                    disabled={resetStatus === 'loading'}
                >
                    Zurück zur Anmeldung
                </Button>
            </Box>
        );
    };

    return (
        <div>
            <CssBaseline enableColorScheme />
            <ResetPasswordContainer direction="column" justifyContent="space-between">
                <Card variant="outlined">
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                    >
                        Passwort zurücksetzen
                    </Typography>
                    {renderContent()}
                </Card>
            </ResetPasswordContainer>
            <Footer />
        </div>
    );
}